// Client filter schema — type-driven, mirrors server/filters.js registry.
// control is DERIVED from type by the rail; we only declare type + label + group.
// type: text | number | date | enum | boolean | exists
// enum fields carry `options` for the multi-select.

import { ALL_SECTORS } from './sectors';
// LinkedIn sector taxonomy as filter options (multi-select, contains_any match).
export const SECTOR_OPTIONS = ALL_SECTORS.map(s => ({ value: s, label: s }));

const SIGNAL_TYPES = [
  { value: 'funding', label: 'Funding' },
  { value: 'leadership_change', label: 'Leadership change' },
  { value: 'hiring', label: 'Hiring' },
  { value: 'tech_change', label: 'Tech change' },
  { value: 'news', label: 'News' },
];
const APPROVAL = [{ value: 'approved', label: 'Approved' }, { value: 'pending', label: 'Pending' }];
const CAMPAIGN_STATUS = [
  { value: 'pending', label: 'Pending' }, { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' }, { value: 'completed', label: 'Completed' },
];

export const CONTACT_SCHEMA = {
  groups: ['Contact', 'Score', 'Company', 'Signals', 'Sequence', 'Campaign', 'Engagement', 'Notes & Alerts', 'Integrations'],
  fields: [
    // Contact
    { key: 'job_title', label: 'Job title', group: 'Contact', type: 'text' },
    { key: 'contact_type', label: 'Contact type', group: 'Contact', type: 'enum', dropdown: 'contact_type' },
    { key: 'full_name', label: 'Name', group: 'Contact', type: 'text' },
    { key: 'location', label: 'Location', group: 'Contact', type: 'text', countryMulti: true },
    { key: 'has_email', label: 'Has email', group: 'Contact', type: 'boolean' },
    { key: 'has_linkedin_url', label: 'Has LinkedIn', group: 'Contact', type: 'boolean' },
    { key: 'has_phone', label: 'Has phone', group: 'Contact', type: 'boolean' },
    { key: 'enriched', label: 'Enriched', group: 'Contact', type: 'boolean' },
    { key: 'enriched_at', label: 'Enriched date', group: 'Contact', type: 'date' },
    { key: 'email_verification_result', label: 'Email verification', group: 'Contact', type: 'enum',
      options: [{ value: 'deliverable', label: 'Deliverable' }, { value: 'risky', label: 'Risky' }, { value: 'undeliverable', label: 'Undeliverable' }, { value: 'unknown', label: 'Unknown' }] },
    { key: 'source', label: 'Source', group: 'Contact', type: 'enum',
      options: [{ value: 'apollo', label: 'Apollo' }, { value: 'exa', label: 'Exa' }, { value: 'csv', label: 'CSV' }] },
    { key: 'keywords', label: 'Keywords', group: 'Contact', type: 'text' },
    { key: 'created_at', label: 'Added date', group: 'Contact', type: 'date' },
    // Score
    { key: 'score', label: 'Total score', group: 'Score', type: 'number', min: 0, max: 10 },
    { key: 'icp_score', label: 'ICP fit', group: 'Score', type: 'number', min: 0, max: 10 },
    { key: 'signal_score', label: 'Signal score', group: 'Score', type: 'number', min: 0, max: 10 },
    { key: 'enrichment_score', label: 'Enrichment score', group: 'Score', type: 'number', min: 0, max: 10 },
    { key: 'scored_at', label: 'Scored date', group: 'Score', type: 'date' },
    // Company (cross-entity)
    { key: 'company.sector', label: 'Sector', group: 'Company', type: 'text', dropdownOptions: SECTOR_OPTIONS },
    { key: 'company.location', label: 'Company location', group: 'Company', type: 'text', countryMulti: true },
    { key: 'company.size', label: 'Company size', group: 'Company', type: 'text' },
    { key: 'company.fit_score', label: 'Company fit', group: 'Company', type: 'number', min: 0, max: 10 },
    { key: 'company.swept', label: 'Company scanned', group: 'Company', type: 'boolean' },
    { key: 'company.has_website', label: 'Company has website', group: 'Company', type: 'boolean' },
    // Signals
    { key: 'signal.exists', label: 'Has any signal', group: 'Signals', type: 'exists' },
    { key: 'signal.type', label: 'Signal type', group: 'Signals', type: 'enum', options: SIGNAL_TYPES },
    { key: 'signal.age_days', label: 'Signal within', group: 'Signals', type: 'date' },
    { key: 'signal.min_relevance', label: 'Min relevance', group: 'Signals', type: 'number', min: 1, max: 10 },
    // Sequence
    { key: 'has_sequence', label: 'Has sequence', group: 'Sequence', type: 'boolean' },
    { key: 'sequence_status', label: 'Sequence status', group: 'Sequence', type: 'enum', options: APPROVAL },
    { key: 'email_channel_status', label: 'Email approval', group: 'Sequence', type: 'enum', options: APPROVAL },
    { key: 'linkedin_channel_status', label: 'LinkedIn approval', group: 'Sequence', type: 'enum', options: APPROVAL },
    { key: 'has_email_sequence', label: 'Has email sequence', group: 'Sequence', type: 'boolean' },
    { key: 'has_linkedin_sequence', label: 'Has LinkedIn sequence', group: 'Sequence', type: 'boolean' },
    { key: 'in_email_batch', label: 'In email batch', group: 'Sequence', type: 'exists' },
    { key: 'in_linkedin_batch', label: 'In LinkedIn batch', group: 'Sequence', type: 'exists' },
    { key: 'pushed', label: 'Pushed to ManyReach', group: 'Sequence', type: 'boolean' },
    // Campaign (native)
    { key: 'in_campaign', label: 'In campaign', group: 'Campaign', type: 'exists', relation: 'campaign' },
    { key: 'campaign_status', label: 'Campaign status', group: 'Campaign', type: 'enum', options: CAMPAIGN_STATUS, relation: 'campaign' },
    { key: 'campaign_step', label: 'Campaign step', group: 'Campaign', type: 'number', min: 0, max: 20, relation: 'campaign' },
    { key: 'campaign_replied', label: 'Replied in campaign', group: 'Campaign', type: 'exists', relation: 'campaign' },
    { key: 'campaign_bounced', label: 'Bounced in campaign', group: 'Campaign', type: 'exists', relation: 'campaign' },
    { key: 'campaign_unsubscribed', label: 'Unsubscribed in campaign', group: 'Campaign', type: 'exists', relation: 'campaign' },
    { key: 'campaign_active_recent', label: 'Active in campaign within', group: 'Campaign', type: 'date', relation: 'campaign' },
    { key: 'has_persona', label: 'Has sender persona', group: 'Campaign', type: 'exists' },
    // Engagement
    { key: 'has_been_emailed', label: 'Has been emailed', group: 'Engagement', type: 'exists' },
    { key: 'has_replied', label: 'Has replied', group: 'Engagement', type: 'exists' },
    { key: 'last_emailed', label: 'Last emailed', group: 'Engagement', type: 'date' },
    { key: 'last_opened', label: 'Last opened', group: 'Engagement', type: 'date' },
    { key: 'opted_out', label: 'Opted out', group: 'Engagement', type: 'exists' },
    // CRM
    { key: 'has_notes', label: 'Has notes', group: 'Notes & Alerts', type: 'exists' },
    { key: 'has_alert', label: 'Has alert', group: 'Notes & Alerts', type: 'exists' },
    { key: 'crm_push_target', label: 'CRM destination', group: 'Integrations', type: 'enum', options: [
      { value: 'pipedrive', label: 'Pipedrive' },
      { value: 'zoho_bigin', label: 'Zoho Bigin' },
      { value: 'zoho_crm', label: 'Zoho CRM' },
      { value: 'hubspot', label: 'HubSpot' },
    ] },
    { key: 'crm_push_status', label: 'CRM sync status', group: 'Integrations', type: 'enum',
      options: [{ value: 'success', label: 'Pushed' }, { value: 'pending', label: 'Pending' }, { value: 'failed', label: 'Failed' }] },
    { key: 'crm_pushed_at', label: 'CRM synced date', group: 'Integrations', type: 'date' },
    { key: 'status', label: 'Contact status', group: 'Contact', type: 'enum',
      options: [{ value: 'active', label: 'Active' }, { value: 'archived', label: 'Archived' }] },
  ],
  quick: ['score', 'company.sector', 'has_sequence', 'campaign_status'],
};

export const COMPANY_SCHEMA = {
  groups: ['Company', 'Signals', 'Contacts'],
  fields: [
    { key: 'sector', label: 'Sector', group: 'Company', type: 'text', dropdownOptions: SECTOR_OPTIONS },
    { key: 'location', label: 'Location', group: 'Company', type: 'text', countryMulti: true },
    { key: 'size', label: 'Size', group: 'Company', type: 'text', dropdown: 'size' },
    { key: 'fit_score', label: 'Fit score', group: 'Company', type: 'number', min: 0, max: 10 },
    { key: 'status', label: 'Status', group: 'Company', type: 'enum',
      options: [{ value: 'researched', label: 'Researched' }, { value: 'active', label: 'Active' }] },
    { key: 'has_website', label: 'Has website', group: 'Company', type: 'boolean' },
    { key: 'swept', label: 'Scanned for signals', group: 'Company', type: 'boolean' },
    { key: 'swept_at', label: 'Last scanned', group: 'Company', type: 'date' },
    { key: 'sweep_count', label: 'Times scanned', group: 'Company', type: 'number', min: 0, max: 50 },
    { key: 'signal.exists', label: 'Has any signal', group: 'Signals', type: 'exists' },
    { key: 'signal.type', label: 'Signal type', group: 'Signals', type: 'enum', options: SIGNAL_TYPES },
    { key: 'signal.age_days', label: 'Signal within', group: 'Signals', type: 'date' },
    { key: 'signal.min_relevance', label: 'Min relevance', group: 'Signals', type: 'number', min: 1, max: 10 },
    { key: 'has_contacts', label: 'Has contacts', group: 'Contacts', type: 'exists' },
    { key: 'contact_count', label: 'Contact count', group: 'Contacts', type: 'number', min: 0, max: 100 },
    { key: 'has_notes', label: 'Has notes', group: 'Contacts', type: 'exists' },
  ],
  quick: ['sector', 'swept', 'signal.type'],
};

export const SEQUENCE_SCHEMA = {
  groups: ['Sequence', 'Email channel', 'LinkedIn channel', 'Push destination', 'Score', 'Company'],
  fields: [
    // Sequence (content lifecycle)
    { key: 'has_sequence', label: 'Has sequence', group: 'Sequence', type: 'boolean' },
    { key: 'sequence_status', label: 'Approval status', group: 'Sequence', type: 'enum', options: [
      { value: 'approved', label: 'Approved' }, { value: 'pending', label: 'Pending' } ] },
    { key: 'sequence_age_days', label: 'Generated within', group: 'Sequence', type: 'date' },
    { key: 'sequence_version', label: 'Version', group: 'Sequence', type: 'number', min: 1, max: 20 },
    { key: 'has_email_sequence', label: 'Has email sequence', group: 'Sequence', type: 'boolean' },
    { key: 'has_linkedin_sequence', label: 'Has LinkedIn sequence', group: 'Sequence', type: 'boolean' },
    // Email channel
    { key: 'email_channel_status', label: 'Email approval', group: 'Email channel', type: 'enum', options: [
      { value: 'approved', label: 'Approved' }, { value: 'pending', label: 'Pending' } ] },
    { key: 'has_email_channel', label: 'Has email channel', group: 'Email channel', type: 'boolean' },
    { key: 'in_email_batch', label: 'In email batch', group: 'Email channel', type: 'exists' },
    // LinkedIn channel
    { key: 'linkedin_channel_status', label: 'LinkedIn approval', group: 'LinkedIn channel', type: 'enum', options: [
      { value: 'approved', label: 'Approved' }, { value: 'pending', label: 'Pending' } ] },
    { key: 'has_linkedin_channel', label: 'Has LinkedIn channel', group: 'LinkedIn channel', type: 'boolean' },
    { key: 'in_linkedin_batch', label: 'In LinkedIn batch', group: 'LinkedIn channel', type: 'exists' },
    // Push destination (the corrected, multi-destination view)
    { key: 'in_campaign', label: 'In CE campaign', group: 'Push destination', type: 'exists', relation: 'campaign' },
    // Score
    { key: 'score', label: 'Total score', group: 'Score', type: 'number', min: 0, max: 10 },
    { key: 'icp_score', label: 'ICP fit', group: 'Score', type: 'number', min: 0, max: 10 },
    // Company
    { key: 'company.sector', label: 'Sector', group: 'Company', type: 'text', dropdownOptions: SECTOR_OPTIONS },
    { key: 'company.location', label: 'Company location', group: 'Company', type: 'text', countryMulti: true },
  ],
  quick: ['has_sequence', 'sequence_status', 'email_channel_status', 'in_campaign'],
};

export const DATE_PRESETS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

const _schema = { CONTACT_SCHEMA, COMPANY_SCHEMA, SEQUENCE_SCHEMA, DATE_PRESETS };
export default _schema;
